import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ShieldCheck, AlertCircle, Activity, Database, Cpu, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_COLORS = {
  compliant: { bg: "bg-emerald-500/15", tx: "text-emerald-400", border: "border-emerald-500/30" },
  limited_risk: { bg: "bg-cyan-500/15", tx: "text-cyan-400", border: "border-cyan-500/30" },
  wellness_exempt: { bg: "bg-violet-500/15", tx: "text-violet-400", border: "border-violet-500/30" },
  structural_alignment: { bg: "bg-amber-500/15", tx: "text-amber-400", border: "border-amber-500/30" },
  in_progress: { bg: "bg-gray-500/15", tx: "text-gray-400", border: "border-gray-500/30" },
};
const STATUS_LABEL = {
  compliant: { bg: "Съответства", en: "Compliant" },
  limited_risk: { bg: "Огр. риск (декларирано)", en: "Limited risk (declared)" },
  wellness_exempt: { bg: "Wellness exempt", en: "Wellness exempt" },
  structural_alignment: { bg: "Структурно съответствие", en: "Structural alignment" },
  in_progress: { bg: "В процес", en: "In progress" },
};

/**
 * DoctorComplianceAuditPanel — GDPR + EU AI Act + MDR audit dashboard
 * за лекарите. Показва:
 * - Compliance framework статуси
 * - Aggregate AI decisions (own + patients)
 * - Per-feature и per-model breakdown
 * - Retention policies
 * - Recent errors
 */
export default function DoctorComplianceAuditPanel() {
  const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
        const { data: r } = await axios.get(`${API}/doctor/compliance/audit`, {
          params: { days },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancel) setData(r);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [API, days]);

  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;

  return (
    <div className={cardCls} data-testid="doctor-compliance-audit">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4"
        data-testid="doctor-compliance-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 via-cyan-600 to-blue-700 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-bold flex items-center gap-1.5">
              {bg ? "GDPR / EU AI Act одит" : "GDPR / EU AI Act audit"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg
                ? "Регулаторен статус, ретеншън, AI прозрачност"
                : "Regulatory status, retention, AI transparency"}
            </p>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {open && (
      <div className="px-4 pb-4 space-y-3">
      {loading ? (
        <div className="py-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          {bg ? "Зареждам..." : "Loading..."}
        </div>
      ) : err ? (
        <div className={`p-3 rounded-lg ${isDark ? "bg-rose-500/10 text-rose-300" : "bg-rose-50 text-rose-700"} text-sm`}>
          {bg ? "Грешка: " : "Error: "} {err}
        </div>
      ) : data && (
        <>
          {/* Window selector + summary */}
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Период" : "Window"}
              </span>
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                className={`text-xs px-2 py-1 rounded-md border ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
                data-testid="audit-days-select"
              >
                <option value={7}>{bg ? "7 дни" : "7 days"}</option>
                <option value={30}>{bg ? "30 дни" : "30 days"}</option>
                <option value={90}>{bg ? "90 дни" : "90 days"}</option>
                <option value={365}>{bg ? "12 месеца" : "12 months"}</option>
              </select>
            </div>
            <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "Пациенти в обхват: " : "Patients in scope: "}
              <span className="font-bold">{data.scope.patients_in_scope}</span>
              {" · "}
              {bg ? "AI извиквания: " : "AI calls: "}
              <span className="font-bold">{data.totals.ai_decisions}</span>
            </div>
          </div>

          {/* Compliance frameworks */}
          <section data-testid="audit-frameworks">
            <div className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "Регулаторни рамки" : "Regulatory frameworks"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(data.frameworks || []).map((f) => {
                const sc = STATUS_COLORS[f.status] || STATUS_COLORS.in_progress;
                const sl = STATUS_LABEL[f.status] || { bg: f.status, en: f.status };
                return (
                  <div
                    key={f.id}
                    className={`rounded-lg border p-2.5 ${sc.border} ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                    data-testid={`audit-framework-${f.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[11px] font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                        {bg ? f.name_bg : f.name_en}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${sc.bg} ${sc.tx}`}>
                        {bg ? sl.bg : sl.en}
                      </span>
                    </div>
                    <p className={`text-[10px] leading-tight ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {bg ? f.evidence_bg : f.evidence_en}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Per-feature breakdown */}
          {(data.feature_breakdown || []).length > 0 && (
            <section data-testid="audit-feature-breakdown">
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                <Activity className="w-3 h-3" />
                {bg ? "AI извиквания по функция" : "AI calls by feature"}
              </div>
              <div className={`rounded-lg border ${isDark ? "border-white/10" : "border-gray-200"} overflow-hidden`}>
                <table className="w-full text-[11px]">
                  <thead className={isDark ? "bg-white/5 text-gray-400" : "bg-gray-50 text-gray-600"}>
                    <tr>
                      <th className="text-left px-3 py-1.5 font-semibold">{bg ? "Функция" : "Feature"}</th>
                      <th className="text-right px-3 py-1.5 font-semibold">{bg ? "Извиквания" : "Calls"}</th>
                      <th className="text-right px-3 py-1.5 font-semibold">{bg ? "Грешки" : "Errors"}</th>
                      <th className="text-right px-3 py-1.5 font-semibold">{bg ? "Редакции" : "Redactions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.feature_breakdown.map((f, i) => (
                      <tr key={i} className={i % 2 ? (isDark ? "bg-white/0" : "bg-white") : (isDark ? "bg-white/[0.02]" : "bg-gray-50/50")}>
                        <td className={`px-3 py-1.5 font-mono text-[10.5px] ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          {f.feature_id}
                        </td>
                        <td className="px-3 py-1.5 text-right font-bold">{f.count}</td>
                        <td className={`px-3 py-1.5 text-right ${f.errors > 0 ? "text-rose-500 font-bold" : "text-gray-500"}`}>
                          {f.errors}
                        </td>
                        <td className="px-3 py-1.5 text-right text-emerald-500">{f.redactions_applied}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Per-model breakdown */}
          {(data.model_breakdown || []).length > 0 && (
            <section data-testid="audit-model-breakdown">
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                <Cpu className="w-3 h-3" />
                {bg ? "Използвани AI модели" : "AI models used"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.model_breakdown.map((m, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold px-2 py-1 rounded-full ${isDark ? "bg-white/5 text-gray-200" : "bg-gray-100 text-gray-700"}`}
                  >
                    <span className="font-mono">{m.model}</span>
                    <span className="text-gray-500">×{m.count}</span>
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Retention policies */}
          <section data-testid="audit-retention">
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              <Database className="w-3 h-3" />
              {bg ? "Политики за съхранение" : "Retention policies"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {(data.retention_policies || []).map((p) => (
                <div
                  key={p.data_class}
                  className={`flex items-center justify-between text-[11px] px-2.5 py-1.5 rounded-md ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                  data-testid={`audit-retention-${p.data_class}`}
                >
                  <span>{bg ? p.label_bg : p.label_en}</span>
                  <span className={`font-bold ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>{p.retention_days} {bg ? "дни" : "days"}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Recent errors */}
          {(data.recent_errors || []).length > 0 && (
            <section data-testid="audit-recent-errors">
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? "text-rose-300" : "text-rose-700"}`}>
                <AlertCircle className="w-3 h-3" />
                {bg ? "Последни грешки" : "Recent errors"}
              </div>
              <ul className="space-y-1">
                {data.recent_errors.map((e, i) => (
                  <li key={i} className={`text-[10.5px] flex items-center justify-between gap-2 px-2 py-1 rounded-md ${isDark ? "bg-rose-500/5" : "bg-rose-50"}`}>
                    <span className="font-mono truncate">{e.feature_id} · {e.model}</span>
                    <span className={`${isDark ? "text-gray-500" : "text-gray-500"} shrink-0`}>{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className={`text-[10px] italic pt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg
              ? "Всички AI извиквания се логват privacy-safely (само хешове на input/output, без raw текст). Точките за достъп: GET /api/me/gdpr/ai-decisions, GET /api/doctor/compliance/audit."
              : "All AI calls are logged privacy-safely (input/output hashes only, no raw text). Access points: GET /api/me/gdpr/ai-decisions, GET /api/doctor/compliance/audit."}
          </p>
        </>
      )}
      </div>
      )}
    </div>
  );
}
